import { useState, useEffect } from "react";
import { FiInstagram, FiYoutube, FiFacebook } from "react-icons/fi";
import { FaTiktok } from "react-icons/fa";
import { getPlatformAnalytics } from "../api/analytics";
import EmptyState from "../components/ds/EmptyState";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";
import StatCell from "../components/ds/StatCell";

const AnalyticsPage = () => {
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const platforms = [
    { id: "instagram", name: "Instagram", icon: FiInstagram },
    { id: "tiktok", name: "TikTok", icon: FaTiktok },
    { id: "youtube", name: "YouTube", icon: FiYoutube },
    { id: "facebook", name: "Facebook", icon: FiFacebook },
  ];

  // Fetch analytics data from HypeAuditor API
  const fetchAnalytics = async (platform) => {
    setLoading(true);
    setError(null);
    try {
      // Call backend endpoint which will proxy to HypeAuditor API
      const response = await getPlatformAnalytics(platform);
      if (response.success) {
        setAnalyticsData(response.data);
      } else {
        setError(response.message || "Failed to fetch analytics data");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch analytics. Please try again.");
      console.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPlatform) {
      fetchAnalytics(selectedPlatform);
    }
  }, [selectedPlatform]);

  const handlePlatformSelect = (platformId) => {
    setSelectedPlatform(platformId);
    setAnalyticsData(null);
  };

  /** "+4% from last month", when the source reports a change. */
  const change = (value) =>
    value ? `${value > 0 ? "+" : ""}${value}% from last month` : undefined;

  const stats = analyticsData
    ? [
        { label: "Followers", value: analyticsData.followers, meta: analyticsData.followersChange },
        { label: "Profile Views", value: analyticsData.profileViews, meta: analyticsData.profileViewsChange },
        { label: "Total Likes", value: analyticsData.likes, meta: analyticsData.likesChange },
        {
          label: "Engagement Rate",
          value: analyticsData.engagementRate,
          text: `${analyticsData.engagementRate?.toFixed(2) ?? "N/A"}%`,
          meta: analyticsData.engagementRateChange,
        },
      ].filter((stat) => stat.value !== undefined)
    : [];
  const details = analyticsData
    ? [
        ["Average Likes per Post", analyticsData.avgLikesPerPost],
        ["Average Comments per Post", analyticsData.avgCommentsPerPost],
        ["Average Views per Post", analyticsData.avgViewsPerPost],
        ["Top Audience Age", analyticsData.audienceAge],
        ["Audience Gender", analyticsData.audienceGender],
        ["Top Audience Location", analyticsData.audienceLocation],
      ].filter(([, value]) => Boolean(value))
    : [];
  const platformName = platforms.find((p) => p.id === selectedPlatform)?.name;

  return (
    <Page>
      <PageHeader
        eyebrow="Analytics"
        title="Social Media Analytics"
        subtitle="Connect and analyze your social media profiles."
      />

      <section className="flex flex-col gap-space-24">
        <h2 className="text-title-3 text-label-primary">Select Platform</h2>
        <div className="grid grid-cols-2 gap-space-16 min-[900px]:grid-cols-4 lg:gap-space-24">
          {platforms.map((platform) => {
            const PlatformIcon = platform.icon;
            const isSelected = selectedPlatform === platform.id;
            return (
              <button
                key={platform.id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => handlePlatformSelect(platform.id)}
                className={`flex flex-col items-center gap-space-12 rounded-radius-lg bg-bg-raised p-space-16 outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
                  isSelected ? "shadow-[inset_0_0_0_1px_var(--color-accent-default)]" : ""
                }`}
              >
                <span className="flex size-[48px] items-center justify-center rounded-14 bg-fill-secondary text-label-secondary">
                  <PlatformIcon className="size-6" />
                </span>
                <span className="text-headline text-label-primary">{platform.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      {!selectedPlatform && (
        <EmptyState
          icon="chart"
          title="Select a platform to view analytics"
          message="Choose from Instagram, TikTok, YouTube, or Facebook to see detailed analytics."
        />
      )}

      {loading && <p className="text-subheadline text-label-secondary">Loading analytics data...</p>}

      {error && !loading && (
        <p className="rounded-radius-sm bg-status-danger-bg px-space-16 py-space-12 text-subheadline text-status-danger">{error}</p>
      )}

      {analyticsData && !loading && (
        <>
          {stats.length > 0 && (
            <div className="grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2 lg:gap-space-24 xl:grid-cols-4">
              {stats.map((stat) => (
                <StatCell
                  key={stat.label}
                  label={stat.label}
                  value={stat.text || stat.value?.toLocaleString() || "N/A"}
                  meta={change(stat.meta)}
                />
              ))}
            </div>
          )}
          {details.length > 0 && (
            <section className="flex flex-col gap-space-16">
              <h2 className="text-title-3 text-label-primary">{platformName} Analytics Details</h2>
              <div className="grid grid-cols-1 gap-space-16 min-[700px]:grid-cols-2 lg:gap-space-24">
                {details.map(([label, value]) => (
                  <StatCell key={label} label={label} value={value.toLocaleString()} />
                ))}
              </div>
            </section>
          )}
          {!stats.length && !details.length && (
            <EmptyState
              icon="chart"
              title="No analytics data available"
              message={`We couldn't find analytics for ${platformName}.`}
            />
          )}
        </>
      )}
    </Page>
  );
};

export default AnalyticsPage;

